import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import LZString from "lz-string";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  setFileContent,
  setUploaderFlag,
  clearAllMapState,
} from "../reducers/MapSlice";
import parse from "../utils/file_to_geojson";
import { toast } from "react-toastify";
import CoordinatesInputField from "./CoordinatesInputField";
import { useAdmin } from "../hooks/useAdmin";
import axios from "../api/axios_client";
import { url } from "../api/axios_client";
import { CircularProgress } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import InfoRounded from "@mui/icons-material/InfoRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationDialog from "./AdminDashboard/DeleteConfirmationDialog";
import { Box } from "@mui/material";
import CropIcon from "@mui/icons-material/Crop";
import { useTranslation } from "react-i18next";
import { Card, CardContent, IconButton } from "@mui/material";

export default function CoordinatesInput({ disableFileUpload }) {
  const dispatch = useDispatch();
  const isAdmin = useAdmin();
  const [open, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [country, setCountry] = useState("");
  const [selectedInternalCountry, setSelectedInternalCountry] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState("");
  const [selectedFeature, setSelectedFeature] = useState([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [loading, setCountryUploadLoading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectionMode, setSelectionMode] = useState(null);
  const [fileSelected, setFileSelected] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState(null);
  const [currentlySelectedFileId, setCurrentlySelectedFileId] = useState(null);

  const [deleteLoading, setDeleteLoading] = useState(false);
  const { t, i18n } = useTranslation();

  const countries = [
    "Algeria",
    "Bahrain",
    "Comoros",
    "Djibouti",
    "Egypt",
    "Iraq",
    "Jordan",
    "Kuwait",
    "Lebanon",
    "Libya",
    "Mauritania",
    "Morocco",
    "Oman",
    "Palestine",
    "Qatar",
    "Saudi Arabia",
    "Somalia",
    "Sudan",
    "Syria",
    "Tunisia",
    "United Arab Emirates",
    "Yemen",
  ];
  const [availableFiles, setAvailableFiles] = useState({});

  // Helper function to get error message based on language
  const getErrorMessage = (error, msg = "") => {
    if (error.response?.data) {
      const isArabic = i18n.language === "ar";
      return isArabic
        ? error.response.data.message_ar || error.response.data.message
        : error.response.data.message;
    }
    if (msg) return msg;
    return "An error occurred";
  };

  // Helper function to clear drawn shapes and map state
  const clearDrawnShapesAndMapState = () => {
    localStorage.removeItem("mapFileContent");
    dispatch(clearAllMapState());
    dispatch(setUploaderFlag(false));

    // Trigger custom event to clear shapes from map
    window.dispatchEvent(new CustomEvent("clearDrawnShapes"));
  };

  useEffect(() => {
    const fetchFiles = async () => {
      if (!selectedInternalCountry) return; // Only fetch if a country is selected

      setLoadingFiles(true);
      try {
        const response = await axios.get(`${url}/areas`, {
          params: {
            country: selectedInternalCountry, // Use selectedInternalCountry instead of country
          },
        });

        // Simplify the data structure since we're already filtering by country
        const filesArray = response.data;
        setAvailableFiles({ [selectedInternalCountry]: filesArray });
      } catch (error) {
        console.error("Error fetching files:", error);
        toast.error("Failed to fetch available files");
      } finally {
        setLoadingFiles(false);
      }
    };

    fetchFiles();
  }, [selectedInternalCountry]);

  useEffect(() => {
    const processSubset = async () => {
      if (selectionMode === "subset") {
        try {
          setLoadingFeatures(true);
          const fileContent = JSON.parse(
            localStorage.getItem("tempFileContent")
          );

          // Check if we're dealing with internal file (has attributes property)
          const storedAttributes = localStorage.getItem("attributesFeatures");
          if (storedAttributes) {
            const attributes = Object.keys(JSON.parse(storedAttributes)).filter(
              (key) =>
                Array.isArray(JSON.parse(storedAttributes)[key]) &&
                JSON.parse(storedAttributes)[key].length > 0 &&
                isNaN(key)
            );
            console.log("attrs:", attributes);
            setAvailableAttributes(attributes);
            if (attributes.length > 0) {
              setSelectedAttribute(attributes[0]); // Set first attribute
              const features = JSON.parse(storedAttributes)[attributes[0]];
              setAvailableFeatures(features);
              // Reset selected features when processing subset
              setSelectedFeature([]);
            }
          } else {
            if (
              !fileContent.features[0].properties ||
              Object.keys(fileContent.features[0].properties).length === 0
            ) {
              toast.error("File doesn't have subset attributes");
              return;
            }
            // COMMENTED OUT: parseOnly request is no longer needed since attributes come from parseFile
            // Handle uploaded file case
            // const response = await axios.post(
            //   `${url}/areas?parseOnly`,
            //   {
            //     geo: JSON.stringify(fileContent),
            //   },
            //   {
            //     headers: {
            //       "Content-Type": "application/json",
            //     },
            //   }
            // );

            // const attributes = Object.keys(response.data).filter(
            //   (key) =>
            //     Array.isArray(response.data[key]) &&
            //     response.data[key].length > 0 &&
            //     isNaN(key)
            // );

            // console.log("response.data:", attributes);

            // const defaultFeatures = response.data[attributes[0]];
            // console.log("default features:", defaultFeatures);
            // localStorage.setItem(
            //   "attributesFeatures",
            //   JSON.stringify(response.data)
            // );
            // setAvailableAttributes(attributes);
            // setAvailableFeatures(defaultFeatures);
            // if (attributes.length > 0) {
            //   setSelectedAttribute(attributes[0]); // Set first attribute
            // }
            // // Reset selected features when processing subset
            // setSelectedFeature([]);
          }
        } catch (error) {
          console.error("Error validating features:", error);
          toast.error("Failed to validate features");
        } finally {
          setLoadingFeatures(false);
        }
      }
    };

    if (selectionMode === "subset") {
      processSubset();
    }
  }, [selectionMode]);

  const handleDelete = (e, area) => {
    e.stopPropagation();
    setAreaToDelete(area);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleteLoading(true); // Start loading
      const response = await axios.delete(`${url}/areas/${areaToDelete.id}`, {
        headers: {
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem("user")).jwt
          }`,
        },
      });

      if (response.status === 200) {
        toast.success("Area deleted successfully");
        const updatedFiles = { ...availableFiles };
        updatedFiles[selectedInternalCountry] = updatedFiles[
          selectedInternalCountry
        ].filter((file) => file.id !== areaToDelete.id);
        setAvailableFiles(updatedFiles);
      }
    } catch (error) {
      console.error("Error deleting area:", error);
      toast.error("Failed to delete area");
    } finally {
      setDeleteLoading(false); // Stop loading
      setDeleteDialogOpen(false);
      setAreaToDelete(null);
    }
  };

  const handleInternalCountryChange = (event) => {
    setSelectedInternalCountry(event.target.value);
    setSearchTerm("");
    setSelectedAttribute("");
    setSelectedFeature([]);
    setAvailableAttributes([]);
    setAvailableFeatures([]);
  };

  const handleInternalFileSelect = async (file) => {
    try {
      setLoadingFeatures(true);
      const response = await axios.get(`${url}/areas/${file.id}`);

      if (!response.data) {
        console.error("Invalid GeoJSON received:", response.data);
        toast.error("Invalid GeoJSON file format");
        return;
      }

      localStorage.setItem(
        "tempFileContent",
        JSON.stringify(response.data.geo)
      );
      localStorage.setItem(
        "attributesFeatures",
        file.attributes ? JSON.stringify(file.attributes) : []
      );
      setFileSelected(true);
      setSelectionMode(null);
      setCurrentlySelectedFileId(file.id); // Set the ID of the selected file for styling
    } catch (error) {
      console.error("Error loading file:", error);
      // toast.error("Failed to load file");
      let errMsg = getErrorMessage(error, "Failed to load file");
      toast.error(errMsg);
      setFileSelected(false);
    } finally {
      setLoadingFeatures(false);
    }
  };

  const handleCountryUpload = async () => {
    if (!selectedFile || !country) {
      toast.warn("Please select both a country and a file");
      return;
    }

    try {
      const toastId = toast.loading("Uploading Area File to Repository");
      setCountryUploadLoading(!loading);
      const ext = selectedFile.name.split(".").pop();

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("name", name);
      formData.append("description", description);
      formData.append("country", country);

      try {
        const response = await axios.post(`${url}/areas`, formData, {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(localStorage.getItem("user")).jwt
            }`,
            "Content-Type": "multipart/form-data",
          },
        });

        console.log("UPLOAD LOADING:", loading);
        if (response.status === 200) {
          toast.update(toastId, {
            render: "Area File Uploaded to Repository Successfully",
            type: "success",
            isLoading: false,
            autoClose: 3000,
          });
          handleClose();
          setSelectedInternalCountry("");
          setSearchTerm("");
          setSelectedAttribute("");
          setSelectedFeature([]);
          setAvailableAttributes([]);
          setAvailableFeatures([]);
          // Refresh available files
          // const fetchFiles = async () => {
          //   const response = await axios.get(`${url}/areas`);
          //   const filesMap = {};
          //   response.data.forEach(area => {
          //     if (area.attributes && area.attributes.ADM0_EN) {
          //       const country = area.name;
          //       if (!filesMap[country]) {
          //         filesMap[country] = [];
          //       }
          //       filesMap[country].push(area);
          //     }
          //   });
          //   setAvailableFiles(filesMap);
          // };
          // fetchFiles();
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        toast.update(toastId, {
          render: getErrorMessage(error),
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        setCountryUploadLoading(false);
      }
    } catch (error) {
      console.error("Error processing file:", error);
      toast.error("Failed to process file");
    } finally {
      setCountryUploadLoading(false);
    }
  };

  const handleFileChosen = async (e) => {
    // Clear existing drawn shapes and map state
    clearDrawnShapesAndMapState();

    setAvailableAttributes([]);
    setAvailableFeatures([]);
    setSelectedAttribute("");
    setSelectedFeature([]);
    localStorage.removeItem("attributesFeatures");
    let file = e.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error("File size exceeds 5MB limit. Please select a smaller file.");
      return;
    }

    const ext = file.name.split(".").pop().toLowerCase();
    setSelectionMode(null);

    const formData = new FormData();
    formData.append("file", file);
    let toastId;

    try {
      toastId = toast.loading("Processing Area File...");
      const response = await axios.post(`${url}/areas?parseFile`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (!response.data) {
        console.error("Invalid GeoJSON received:", response.data);
        toast.error(
          "Area file is Invalid. Accepted files are: .dxf, .gdb, .kml, .wkt, .geojson, .zip, .gpkg"
        );
        toast.update(toastId, {
          render:
            "Area file is Invalid. Accepted files are: .dxf, .gdb, .kml, .wkt, .geojson, .zip, .gpkg",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        return;
      }
      console.log("geo response", response);
      localStorage.setItem(
        "tempFileContent",
        JSON.stringify(response.data.geo)
      );

      // Store attributes if they exist in the response
      if (response.data.attributes) {
        localStorage.setItem(
          "attributesFeatures",
          JSON.stringify(response.data.attributes)
        );
      }

      setFileSelected(true);
      toast.update(toastId, {
        render: "Area File Processed Successfully",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (err) {
      dispatch(setUploaderFlag(false));
      toast.update(toastId, {
        render:
          getErrorMessage(err) ||
          "An error occurred while processing area file",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      setFileSelected(false);
    }

    // parse(file, ext,
    //   async (res) => {
    //     localStorage.setItem('tempFileContent', JSON.stringify(res));
    //   },
    //   () => {
    //     dispatch(setUploaderFlag(false));
    //     toast.warn("file content isn't valid");
    //     setFileSelected(false);
    //   }
    // );
  };

  const handleSelectionSubmit = async () => {
    if (!selectedAttribute || selectedFeature.length === 0) {
      toast.warn("Please select both an attribute and at least one feature");
      return;
    }

    try {
      setLoadingFeatures(true);
      const fileContent = localStorage.getItem("tempFileContent");

      // Prepare the geo data based on the source
      let geoData;
      if (selectedOption === "internal") {
        // For internal repository files, use the geo string directly
        geoData = fileContent;
      } else {
        // For uploaded files, stringify the GeoJSON object
        geoData = fileContent;
      }

      const response = await axios.post(`${url}/areas?select`, {
        geo: geoData,
        attribute: selectedAttribute,
        features: selectedFeature, // Now sending array of features
      });

      // Parse the nested GeoJSON string from the response
      const parsedGeoJSON = JSON.parse(response.data.geo);

      // Validate GeoJSON before dispatching
      if (!parsedGeoJSON || !parsedGeoJSON.type || !parsedGeoJSON.features) {
        console.error("Invalid GeoJSON received:", parsedGeoJSON);
        toast.error("Received invalid GeoJSON from server");
        return;
      }

      // Ensure the GeoJSON structure is correct
      const validGeoJSON = {
        type: "FeatureCollection",
        features: parsedGeoJSON.features.map((feature) => ({
          type: "Feature",
          properties: feature.properties || {},
          geometry: feature.geometry,
        })),
      };

      // Clear drawn shapes before setting new content
      clearDrawnShapesAndMapState();

      dispatch(setFileContent(validGeoJSON));
      dispatch(setUploaderFlag(true));
      localStorage.removeItem("tempFileContent");
      const compressedGeoJson = LZString.compress(JSON.stringify(validGeoJSON));
      localStorage.setItem("mapFileContent", compressedGeoJson);
      handleClose();
    } catch (error) {
      console.error("Error filtering geojson:", error);
      toast.error(getErrorMessage(error) || "Failed to process selection");
    } finally {
      setLoadingFeatures(false);
    }
  };

  const handleAttributeSelect = async (event) => {
    const selectedAttr = event.target.value;
    setSelectedAttribute(selectedAttr);
    setSelectedFeature([]);

    try {
      setLoadingFeatures(true);
      const fileContent = JSON.parse(localStorage.getItem("tempFileContent"));

      const response = await axios.post(
        `${url}/prediction/feature-validation`,
        {
          geo: JSON.stringify(fileContent),
          attribute: selectedAttr,
        }
      );

      if (!response.data || !Array.isArray(response.data.features)) {
        throw new Error("Invalid features response");
      }

      setAvailableFeatures(response.data.features);
    } catch (error) {
      console.error("Error fetching features:", error);
      toast.error("Failed to load features");
    } finally {
      setLoadingFeatures(false);
    }
  };

  const handleAttributeChange = (event) => {
    const selectedAttr = event.target.value;
    setSelectedAttribute(selectedAttr);
    setSelectedFeature([]); // Reset selected features when attribute changes

    // Get the stored attributes and features data
    const attributesFeatures = JSON.parse(
      localStorage.getItem("attributesFeatures")
    );

    if (attributesFeatures && attributesFeatures[selectedAttr]) {
      // Get the values for the selected attribute
      const features = attributesFeatures[selectedAttr];
      setAvailableFeatures(features);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedOption("");
    setCountry("");
    setName("");
    setDescription("");
    setSelectedFile(null);
    setSelectionMode(null);
    setFileSelected(false);
  };

  const handleOptionChange = (option) => {
    localStorage.removeItem("tempFileContent");
    localStorage.removeItem("attributesFeatures");
    setAvailableAttributes([]);
    setAvailableFeatures([]);
    setSelectedAttribute("");
    setSelectedFeature([]);
    setSelectionMode(null);
    setFileSelected(false);
    setCurrentlySelectedFileId(null);
    setSelectedInternalCountry("");
    setSearchTerm("");
    setSelectedOption(option);
  };

  const handleCountryChange = (event) => {
    setCountry(event.target.value);
  };

  const handleUpload = () => {
    if (!selectionMode) {
      toast.warn("Please select an area type (Full Area or Subset)");
      return;
    }

    if (selectionMode === "subset") {
      handleSelectionSubmit();
    } else if (selectionMode === "full") {
      try {
        const fileContent = JSON.parse(localStorage.getItem("tempFileContent"));
        if (!fileContent) {
          toast.error("No file content found");
          return;
        }

        // Parse the GeoJSON string if it's from internal repository
        let geoJsonData = fileContent;
        if (typeof fileContent === "string") {
          geoJsonData = JSON.parse(fileContent);
        }

        // Ensure the GeoJSON structure is correct
        const validGeoJSON = {
          type: "FeatureCollection",
          features: geoJsonData.features.map((feature) => ({
            type: "Feature",
            properties: feature.properties || {},
            geometry: feature.geometry,
          })),
        };

        // Clear drawn shapes before setting new content
        clearDrawnShapesAndMapState();

        dispatch(setFileContent(validGeoJSON));
        dispatch(setUploaderFlag(true));
        localStorage.removeItem("tempFileContent");
        const compressedGeoJson = LZString.compress(
          JSON.stringify(validGeoJSON)
        );
        localStorage.setItem("mapFileContent", compressedGeoJson);
        handleClose();
      } catch (error) {
        console.error("Error processing GeoJSON:", error);
        toast.error("Invalid GeoJSON structure");
      }
    }
  };

  // Update the filteredFiles calculation
  const filteredFiles =
    selectedInternalCountry && availableFiles[selectedInternalCountry]
      ? availableFiles[selectedInternalCountry].filter((file) =>
          file.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : [];

  const currentUser = JSON.parse(localStorage.getItem("user"));

  return (
    <div>
      <div className="search-container" onClick={handleOpen}>
        <Typography>{t("Map.add_area")}</Typography>
        <CropIcon />
      </div>

      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>{t("Map.add_area")}</DialogTitle>
        <DialogContent className="dialog-content">
          <div className="options-container">
            <div
              className={`option-box ${
                selectedOption === "upload" ? "selected-option" : ""
              }`}
              onClick={() => handleOptionChange("upload")}
            >
              <h3 className="option-title">{t("Map.upload_data")}</h3>
            </div>

            <div
              className={`option-box ${
                selectedOption === "internal" ? "selected-option" : ""
              }`}
              onClick={() => handleOptionChange("internal")}
            >
              <h3 className="option-title">
                {t("Map.select_from_repository")}
              </h3>
            </div>

            {isAdmin && (
              <div
                className={`option-box ${
                  selectedOption === "country" ? "selected-option" : ""
                }`}
                onClick={() => handleOptionChange("country")}
              >
                <h3 className="option-title">
                  {t("Map.upload_to_repository")}
                </h3>
              </div>
            )}
          </div>

          {selectedOption === "upload" && (
            <div className="upload-box">
              <input
                type="file"
                onChange={handleFileChosen}
                accept=".dxf,.gdb,.kml,.wkt,.geojson,.zip,.gpkg"
              />
              {loadingFeatures ? (
                <CircularProgress />
              ) : (
                fileSelected &&
                !loadingFeatures && (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        gap: "20px",
                        marginTop: "20px",
                        marginBottom: "20px",
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={() => setSelectionMode("full")}
                        style={{
                          flex: 1,
                          backgroundColor:
                            selectionMode === "full" ? "#1976d2" : "#f0f0f0",
                          color: selectionMode === "full" ? "white" : "black",
                        }}
                      >
                        {t("Map.full_area")}
                      </Button>
                      {localStorage.getItem("attributesFeatures") &&
                        JSON.parse(
                          localStorage.getItem("attributesFeatures") || "{}"
                        ) &&
                        Object.keys(
                          JSON.parse(
                            localStorage.getItem("attributesFeatures") || "{}"
                          )
                        ).length > 0 && (
                          <Button
                            variant="contained"
                            onClick={() => setSelectionMode("subset")}
                            style={{
                              flex: 1,
                              backgroundColor:
                                selectionMode === "subset"
                                  ? "#1976d2"
                                  : "#f0f0f0",
                              color:
                                selectionMode === "subset" ? "white" : "black",
                            }}
                          >
                            {t("Map.subset")}
                          </Button>
                        )}
                    </div>
                    {selectionMode === "subset" &&
                      availableAttributes.length > 0 &&
                      availableFeatures.length > 0 && (
                        <div style={{ marginTop: "20px" }}>
                          <div style={{ marginBottom: "20px" }}>
                            <InputLabel>{t("Map.select_attribute")}</InputLabel>
                            <Select
                              fullWidth
                              size="small"
                              value={selectedAttribute}
                              onChange={handleAttributeChange}
                            >
                              {availableAttributes.map((attr) => (
                                <MenuItem
                                  key={attr}
                                  value={attr}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "#3c93e8 !important",
                                      color: "white",
                                    },
                                    "&.Mui-selected": {
                                      backgroundColor: "#1976d2 !important",
                                      color: "white",
                                      "&:hover": {
                                        backgroundColor: "#1565c0 !important", // Slightly darker blue on hover when selected
                                      },
                                    },
                                  }}
                                >
                                  {attr}
                                </MenuItem>
                              ))}
                            </Select>
                          </div>

                          <div>
                            <InputLabel>{t("Map.select_features")}</InputLabel>
                            <Select
                              fullWidth
                              multiple
                              size="small"
                              value={selectedFeature}
                              onChange={(e) =>
                                setSelectedFeature(e.target.value)
                              }
                              renderValue={(selected) => selected.join(", ")}
                            >
                              {availableFeatures.map((feature) => (
                                <MenuItem
                                  key={feature}
                                  value={feature}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "#3c93e8 !important",
                                      color: "white",
                                    },
                                    "&.Mui-selected": {
                                      backgroundColor: "#1976d2 !important",
                                      color: "white",
                                      "&:hover": {
                                        backgroundColor: "#1565c0 !important", // Slightly darker blue on hover when selected
                                      },
                                    },
                                  }}
                                >
                                  {feature}
                                </MenuItem>
                              ))}
                            </Select>
                          </div>
                        </div>
                      )}
                  </div>
                )
              )}
            </div>
          )}

          {selectedOption === "internal" && (
            <div className="internal-file-box">
              <FormControl
                fullWidth
                variant="outlined"
                style={{ marginBottom: "20px" }}
              >
                <InputLabel>{t("Map.select_country")}</InputLabel>
                <Select
                  value={selectedInternalCountry}
                  onChange={handleInternalCountryChange}
                  label="Select Country"
                >
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {t(`countries.${country}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedInternalCountry && (
                <div className="file-list">
                  <TextField
                    variant="outlined"
                    placeholder={t("Map.search_files")}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ marginBottom: "10px" }}
                  />
                  {loadingFiles ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <div className="file-items">
                      {filteredFiles.map((file, index) => (
                        <Card
                          key={index}
                          variant="outlined"
                          sx={{
                            mb: 1,
                            cursor: "pointer",
                            "&:hover": {
                              boxShadow: 1, // Add slight shadow on hover
                            },
                            height: "80px", // Added fixed height
                            display: "flex", // Added for alignment
                            flexDirection: "column", // Stack content vertically
                            justifyContent: "center", // Center content vertically
                            border:
                              file.id === currentlySelectedFileId
                                ? "2px solid"
                                : "1px solid", // Thicker border if selected
                            borderColor:
                              file.id === currentlySelectedFileId
                                ? "primary.main"
                                : "divider", // Primary color border if selected
                            backgroundColor:
                              file.id === currentlySelectedFileId
                                ? "action.hover"
                                : "background.paper", // Slight background tint if selected
                          }}
                          onClick={() => handleInternalFileSelect(file)}
                        >
                          <CardContent
                            sx={{
                              p: 1,
                              m: 2,
                              "&:last-child": { pb: 1 },
                              width: "250px",
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <InsertDriveFileIcon
                                color="primary"
                                sx={{ flexShrink: 0 }}
                              />
                              <Box flexGrow={1} minWidth={0}>
                                {" "}
                                {/* Allow text to shrink/truncate */}
                                <Typography
                                  variant="body2"
                                  noWrap
                                  title={file.name}
                                  sx={{ fontWeight: "medium" }}
                                >
                                  {file.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  title={file.description}
                                >
                                  {file.description || "No description"}{" "}
                                  {/* Handle missing description */}
                                </Typography>
                              </Box>
                              {(currentUser?.country ===
                                selectedInternalCountry ||
                                Number(currentUser?.role) === 3) && (
                                <IconButton
                                  onClick={(e) => handleDelete(e, file)}
                                  size="small"
                                  color="error"
                                  sx={{ ml: "auto", flexShrink: 0 }} // Push button to the right
                                  aria-label={`Delete ${file.name}`}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  {loadingFeatures ? (
                    <CircularProgress />
                  ) : (
                    fileSelected &&
                    !loadingFeatures && (
                      <div>
                        <div
                          style={{
                            display: "flex",
                            gap: "20px",
                            marginTop: "20px",
                            marginBottom: "20px",
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={() => setSelectionMode("full")}
                            style={{
                              flex: 1,
                              backgroundColor:
                                selectionMode === "full"
                                  ? "#1976d2"
                                  : "#f0f0f0",
                              color:
                                selectionMode === "full" ? "white" : "black",
                            }}
                          >
                            {t("Map.full_area")}
                          </Button>
                          {localStorage.getItem("attributesFeatures") &&
                            JSON.parse(
                              localStorage.getItem("attributesFeatures") || "{}"
                            ) &&
                            Object.keys(
                              JSON.parse(
                                localStorage.getItem("attributesFeatures") ||
                                  "{}"
                              )
                            ).length > 0 && (
                              <Button
                                variant="contained"
                                onClick={() => setSelectionMode("subset")}
                                style={{
                                  flex: 1,
                                  backgroundColor:
                                    selectionMode === "subset"
                                      ? "#1976d2"
                                      : "#f0f0f0",
                                  color:
                                    selectionMode === "subset"
                                      ? "white"
                                      : "black",
                                }}
                              >
                                {t("Map.subset")}
                              </Button>
                            )}
                        </div>

                        {selectionMode === "subset" &&
                          availableAttributes.length > 0 &&
                          availableFeatures.length > 0 && (
                            <div style={{ marginTop: "20px" }}>
                              <div style={{ marginBottom: "20px" }}>
                                <InputLabel>
                                  {t("Map.select_attribute")}
                                </InputLabel>
                                <Select
                                  fullWidth
                                  size="small"
                                  value={selectedAttribute}
                                  onChange={handleAttributeChange}
                                >
                                  {availableAttributes.map((attr) => (
                                    <MenuItem key={attr} value={attr}>
                                      {attr}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </div>

                              <div>
                                <InputLabel>
                                  {t("Map.select_features")}
                                </InputLabel>
                                <Select
                                  fullWidth
                                  multiple
                                  size="small"
                                  value={selectedFeature}
                                  onChange={(e) =>
                                    setSelectedFeature(e.target.value)
                                  }
                                  renderValue={(selected) =>
                                    selected.join(", ")
                                  }
                                >
                                  {availableFeatures.map((feature) => (
                                    <MenuItem key={feature} value={feature}>
                                      {feature}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </div>
                            </div>
                          )}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          )}

          {selectedOption === "country" && (
            <div className="country-upload-box">
              <FormControl
                fullWidth
                variant="outlined"
                sx={{ marginBottom: 2 }}
              >
                <InputLabel>{t("Map.country")}</InputLabel>
                <Select value={country} onChange={handleCountryChange}>
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {t(`countries.${country}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={t("Map.name")}
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ marginBottom: 2 }}
              />

              <TextField
                fullWidth
                label={t("Map.description")}
                variant="outlined"
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{ marginBottom: 2 }}
              />

              <input
                type="file"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                accept=".dxf,.gdb,.kml,.wkt,.geojson,.zip,.gpkg"
                style={{ marginBottom: "20px" }}
              />

              {loading ? (
                <CircularProgress
                  style={{ position: "relative", top: "30px", left: "130px" }}
                />
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleCountryUpload}
                  disabled={!selectedFile || !country || !name || loading}
                >
                  {t("Map.upload_file")}
                </Button>
              )}
            </div>
          )}
          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <Button
              style={{
                width: "100px",
                backgroundColor: "#f0f0f0",
                color: "black",
              }}
              variant="contained"
              onClick={handleClose}
            >
              {t("Map.close")}
            </Button>
            {selectedOption !== "country" && (
              <Button
                style={{ width: "100px" }}
                variant="contained"
                onClick={handleUpload}
              >
                {t("Map.submit")}
              </Button>
            )}
          </div>
          <DeleteConfirmationDialog
            isOpen={deleteDialogOpen}
            onClose={() => !deleteLoading && setDeleteDialogOpen(false)} // Prevent closing during loading
            onConfirm={confirmDelete}
            itemName={areaToDelete?.name || "this area"}
            isLoading={deleteLoading} // Pass the loading state
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
